import { useQuery } from "@apollo/client/react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateVcsColored } from "components/icons/generated";
import TierInfo from "components/TierInfo";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { BillingTierFeature } from "types/generated";
import { downloadFile } from "utils/file";
import { AccountContext } from "views/AccountWrapper";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { showVcsAgentPoolCreateDrawer } from "./components/CreateVcsAgentPoolDrawer";
import { CreateVCSAgentPoolData, GET_VCS_AGENT_POOLS } from "./gql";
import { VCS_AGENT_POOLS_TABLE_CELLS } from "./Table/Cells";
import { SORT_CONFIG, VCS_AGENT_POOLS_TABLE_FILTERS_STORAGE_KEY } from "./Table/constants";
import { useSortingVcsAgentPools } from "./Table/useSortingVcsAgentPools";
import useVCSAgentPoolsTableStore from "./Table/useTableStore";

const VCSAgentPools = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();
  const trackAnalytics = useAnalytics();
  useTitle(`VCS Agent Pools · ${accountName}`);
  useLayoutHeading("VCS agent pools");

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: "VCS agent pools",
    },
  ]);

  const createVCSAgentPool = useCallback(() => {
    showVcsAgentPoolCreateDrawer().then((vcsAgentPool) => {
      if (vcsAgentPool) {
        const { id, config, name } = vcsAgentPool as CreateVCSAgentPoolData;
        if (config) {
          downloadFile(`vcs-agent-pool-${id}.config`, config);
          reportSuccess({ message: `VCS agent pool ${name} successfully created` });
        }
        navigate(`/integrations/vcs-agent-pools/${id}`);
      }
    });
  }, [navigate, reportSuccess]);

  useLayoutActions(
    <Button onPress={createVCSAgentPool} variant="primary">
      Create VCS agent pool
    </Button>
  );

  const { data, loading, error } = useQuery(GET_VCS_AGENT_POOLS);

  const ErrorContent = useErrorHandle(error);

  const { filteredVCSAgentPools } = useSortingVcsAgentPools(data?.vcsAgentPools);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <TierInfo
        type="callout"
        variant="promo"
        feature={BillingTierFeature.PrivateVcs}
        title="VCS agent pools are not supported by your plan"
      >
        You may manage them, but their connections will not be used until you upgrade to a plan that
        supports them.
      </TierInfo>

      <TableSimple
        data={filteredVCSAgentPools}
        loading={loading}
        store={useVCSAgentPoolsTableStore}
        ariaLabel="VCS agent pools list"
        trackAnalytics={trackAnalytics}
        cellPerColumn={VCS_AGENT_POOLS_TABLE_CELLS}
        filtersStorageKey={VCS_AGENT_POOLS_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        emptyState={
          <EmptyState
            icon={EmptystateVcsColored}
            title="You haven't created any VCS agent yet"
            caption={
              <>
                You can actually do it now by clicking on the <em>Create VCS agent pool</em> button
                above.
              </>
            }
          />
        }
      />
    </>
  );
};

export default VCSAgentPools;
